import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { ProposalSendModal, ProposalEmailHistoryModal } from './Deals';

export default function Proposals({ api }) {
  const [proposals, setProposals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  const [editingProposal, setEditingProposal] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  
  // Form states
  const [newProposal, setNewProposal] = useState({
    title: '',
    deal_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    client_address: '',
    description: '',
    items: [],
    terms_conditions: '',
    validity_days: 30,
    template_id: ''
  });
  
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    tax_percent: 0,
    total: 0
  });
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    content: '',
    category: 'commercial',
    variables: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [proposalsRes, templatesRes, dealsRes, statsRes] = await Promise.all([
        api.get('/proposals'),
        api.get('/proposal-templates'),
        api.get('/deals'),
        api.get('/proposals/stats')
      ]);

      setProposals(proposalsRes.data?.items || []);
      setTemplates(templatesRes.data?.items || []);
      setDeals(dealsRes.data?.items || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewProposal({
      title: '',
      deal_id: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      client_company: '',
      client_address: '',
      description: '',
      items: [],
      terms_conditions: '',
      validity_days: 30,
      template_id: ''
    });
    setEditingProposal(null);
  };

  const openEdit = (proposal) => {
    setNewProposal({
      title: proposal.title || '',
      deal_id: proposal.deal_id || '',
      client_name: proposal.client_name || '',
      client_email: proposal.client_email || '',
      client_phone: proposal.client_phone || '',
      client_company: proposal.client_company || '',
      client_address: proposal.client_address || '',
      description: proposal.description || '',
      items: (proposal.items || []).map((it, i) => ({ ...it, key: it.key || `it-${i}` })),
      terms_conditions: proposal.terms_conditions || '',
      validity_days: proposal.validity_days || 30,
      template_id: proposal.template_id || ''
    });
    setNewItem({ description: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, total: 0 });
    setEditingProposal(proposal.id);
    setShowCreateModal(true);
  };

  const handleCreateProposal = async () => {
    try {
      if (!newProposal.title || !newProposal.client_name || !newProposal.client_email) {
        toast.error('Preencha os campos obrigatórios');
        return;
      }

      if (editingProposal) {
        await api.put(`/proposals/${editingProposal}`, newProposal);
        toast.success('Proposta atualizada com sucesso! ✏️');
      } else {
        await api.post('/proposals', newProposal);
        toast.success('Proposta criada com sucesso! 📋');
      }
      
      setShowCreateModal(false);
      resetForm();
      
      fetchData();
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error(editingProposal ? 'Erro ao atualizar proposta' : 'Erro ao criar proposta');
    }
  };

  const handleAcceptProposal = async (proposalId) => {
    try {
      await api.post(`/proposals/${proposalId}/accept`);
      toast.success('Proposta aceita com sucesso! ✅');
      fetchData();
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast.error('Erro ao aceitar proposta');
    }
  };

  const addItemToProposal = () => {
    const total = newItem.quantity * newItem.unit_price * (1 - newItem.discount_percent / 100) * (1 + newItem.tax_percent / 100);
    const itemWithTotal = { ...newItem, total };
    
    setNewProposal(prev => ({
      ...prev,
      items: [...prev.items, itemWithTotal]
    }));
    
    setNewItem({
      description: '',
      quantity: 1,
      unit_price: 0,
      discount_percent: 0,
      tax_percent: 0,
      total: 0
    });
  };

  const removeItemFromProposal = (index) => {
    setNewProposal(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getProposalStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'viewed': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getProposalStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Rascunho';
      case 'sent': return 'Enviada';
      case 'viewed': return 'Visualizada';
      case 'accepted': return 'Aceita';
      case 'rejected': return 'Rejeitada';
      case 'expired': return 'Expirada';
      default: return 'Desconhecido';
    }
  };

  const filteredProposals = (proposals || []).filter(proposal => {
    switch (selectedTab) {
      case 'draft': return proposal.status === 'draft';
      case 'sent': return ['sent', 'viewed'].includes(proposal.status);
      case 'accepted': return proposal.status === 'accepted';
      case 'rejected': return proposal.status === 'rejected';
      default: return true;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Propostas e Contratos</h1>
          <p className="text-slate-600 mt-1">Gerencie propostas comerciais e contratos</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            onClick={() => setShowTemplateModal(true)}
            variant="outline"
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
          >
            📝 Templates
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
          >
            ➕ Nova Proposta
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total de Propostas</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_proposals || 0}</p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                <span className="text-xl">📋</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Taxa de Aceitação</p>
                <p className="text-2xl font-bold text-green-600">{(stats.acceptance_rate || 0).toFixed(1)}%</p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-green-100 to-green-200 rounded-full flex items-center justify-center">
                <span className="text-xl">✅</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Valor Total</p>
                <p className="text-2xl font-bold text-blue-600">
                  R$ {(stats.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Valor Aceito</p>
                <p className="text-2xl font-bold text-orange-600">
                  R$ {(stats.accepted_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-orange-100 to-orange-200 rounded-full flex items-center justify-center">
                <span className="text-xl">🎯</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="draft">Rascunhos</TabsTrigger>
          <TabsTrigger value="sent">Enviadas</TabsTrigger>
          <TabsTrigger value="accepted">Aceitas</TabsTrigger>
          <TabsTrigger value="rejected">Rejeitadas</TabsTrigger>
        </TabsList>

        <TabsContent value={selectedTab} className="space-y-4 mt-6">
          {filteredProposals.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Nenhuma proposta encontrada
                </h3>
                <p className="text-slate-500 mb-4">
                  {selectedTab === 'all' 
                    ? 'Crie sua primeira proposta comercial para começar'
                    : `Nenhuma proposta ${getProposalStatusLabel(selectedTab).toLowerCase()} encontrada`
                  }
                </p>
                {selectedTab === 'all' && (
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                  >
                    📋 Criar Primeira Proposta
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredProposals.map((proposal) => (
                <Card key={proposal.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {proposal.title}
                          </h3>
                          <Badge className={getProposalStatusColor(proposal.status)}>
                            {getProposalStatusLabel(proposal.status)}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {proposal.proposal_number}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600">Cliente</p>
                            <p className="font-medium">{proposal.client_name}</p>
                            {proposal.client_company && (
                              <p className="text-slate-500">{proposal.client_company}</p>
                            )}
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Valor</p>
                            <p className="font-bold text-green-600">
                              R$ {proposal.total_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Criada em</p>
                            <p className="font-medium">
                              {new Date(proposal.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-slate-600">Válida até</p>
                            <p className="font-medium">
                              {proposal.valid_until ? 
                                new Date(proposal.valid_until).toLocaleDateString('pt-BR') : 
                                'Não definido'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-6">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setShowDetailModal(true);
                          }}
                        >
                          👁️ Ver
                        </Button>

                        {['draft', 'sent', 'viewed'].includes(proposal.status) && (
                          <Button 
                            size="sm"
                            onClick={() => setEmailTarget(proposal)}
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            📧 {proposal.status === 'draft' ? 'Enviar' : 'Reenviar'}
                          </Button>
                        )}
                        
                        {(proposal.email_history?.length > 0 || proposal.last_email_body) && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => setHistoryTarget(proposal)}
                            className="text-gray-600"
                          >
                            📤 Ver e-mail
                          </Button>
                        )}

                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(proposal)}
                        >
                          ✏️ Editar
                        </Button>
                        
                        {proposal.status === 'sent' && (
                          <Button 
                            size="sm"
                            onClick={() => handleAcceptProposal(proposal.id)}
                            className="bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            ✅ Aceitar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Proposal Modal */}
      <Dialog open={showCreateModal} onOpenChange={(o) => { setShowCreateModal(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>📋</span>
              <span>{editingProposal ? 'Editar Proposta Comercial' : 'Nova Proposta Comercial'}</span>
            </DialogTitle>
            <DialogDescription>
              {editingProposal ? 'Atualize a proposta e reenvie quando quiser' : 'Crie uma nova proposta para enviar ao cliente'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Título da Proposta *</Label>
                <Input
                  value={newProposal.title}
                  onChange={(e) => setNewProposal(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Proposta para Sistema CRM"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Relacionar com Negócio</Label>
                <Select 
                  value={newProposal.deal_id}
                  onValueChange={(value) => setNewProposal(prev => ({ ...prev, deal_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um negócio (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {deals.map((deal) => (
                      <SelectItem key={deal.id} value={deal.id}>
                        {deal.title} - R$ {deal.value.toLocaleString('pt-BR')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Cliente *</Label>
                  <Input
                    value={newProposal.client_name}
                    onChange={(e) => setNewProposal(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Nome completo"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email do Cliente *</Label>
                  <Input
                    type="email"
                    value={newProposal.client_email}
                    onChange={(e) => setNewProposal(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={newProposal.client_phone}
                    onChange={(e) => setNewProposal(prev => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <Input
                    value={newProposal.client_company}
                    onChange={(e) => setNewProposal(prev => ({ ...prev, client_company: e.target.value }))}
                    placeholder="Nome da empresa"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={newProposal.description}
                  onChange={(e) => setNewProposal(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o que está sendo proposto..."
                  rows={4}
                />
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Template (Opcional)</Label>
                <Select 
                  value={newProposal.template_id}
                  onValueChange={(value) => setNewProposal(prev => ({ ...prev, template_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Validade (dias)</Label>
                <Input
                  type="number"
                  value={newProposal.validity_days}
                  onChange={(e) => setNewProposal(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 30 }))}
                  placeholder="30"
                />
              </div>
              
              {/* Items Section */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Itens da Proposta</Label>
                
                {/* Add Item Form */}
                <Card className="p-4 bg-slate-50">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input
                      placeholder="Descrição do item"
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    />
                    <Input
                      type="number"
                      placeholder="Qtd"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                    />
                    <Input
                      type="number"
                      placeholder="Preço unitário"
                      value={newItem.unit_price}
                      onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                    />
                    <Button 
                      size="sm"
                      onClick={addItemToProposal}
                      disabled={!newItem.description || newItem.unit_price === 0}
                    >
                      ➕ Adicionar
                    </Button>
                  </div>
                </Card>
                
                {/* Items List */}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {newProposal.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.description}</p>
                        <p className="text-xs text-slate-600">
                          {item.quantity}x R$ {item.unit_price.toFixed(2)} = R$ {item.total.toFixed(2)}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeItemFromProposal(index)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        🗑️
                      </Button>
                    </div>
                  ))}
                </div>
                
                {newProposal.items.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border border-green-200">
                    <p className="font-semibold text-green-800">
                      Total: R$ {newProposal.items.reduce((acc, item) => acc + item.total, 0).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Termos e Condições</Label>
                <Textarea
                  value={newProposal.terms_conditions}
                  onChange={(e) => setNewProposal(prev => ({ ...prev, terms_conditions: e.target.value }))}
                  placeholder="Termos e condições da proposta..."
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateProposal}
              className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
            >
              {editingProposal ? '✏️ Salvar Alterações' : '📋 Criar Proposta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/** Proposal send + email history modals */}
      <ProposalSendModal proposal={emailTarget} open={!!emailTarget} onClose={() => setEmailTarget(null)} api={api} onSent={fetchData} />
      <ProposalEmailHistoryModal proposal={historyTarget} open={!!historyTarget} onClose={() => setHistoryTarget(null)} />

      {/* Proposal Detail Modal */}
      {selectedProposal && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span>📋</span>
                  <span>{selectedProposal.title}</span>
                </div>
                <Badge className={getProposalStatusColor(selectedProposal.status)}>
                  {getProposalStatusLabel(selectedProposal.status)}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {selectedProposal.proposal_number} • Cliente: {selectedProposal.client_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informações do Cliente</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p><strong>Nome:</strong> {selectedProposal.client_name}</p>
                    <p><strong>Email:</strong> {selectedProposal.client_email}</p>
                    {selectedProposal.client_phone && (
                      <p><strong>Telefone:</strong> {selectedProposal.client_phone}</p>
                    )}
                    {selectedProposal.client_company && (
                      <p><strong>Empresa:</strong> {selectedProposal.client_company}</p>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Detalhes da Proposta</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p><strong>Valor Total:</strong> R$ {selectedProposal.total_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p><strong>Válida até:</strong> {selectedProposal.valid_until ? new Date(selectedProposal.valid_until).toLocaleDateString('pt-BR') : 'Não definido'}</p>
                    <p><strong>Criada em:</strong> {new Date(selectedProposal.created_at).toLocaleDateString('pt-BR')}</p>
                    {selectedProposal.sent_date && (
                      <p><strong>Enviada em:</strong> {new Date(selectedProposal.sent_date).toLocaleDateString('pt-BR')}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Description */}
              {selectedProposal.description && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Descrição</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{selectedProposal.description}</p>
                  </CardContent>
                </Card>
              )}
              
              {/* Items */}
              {selectedProposal.items && selectedProposal.items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Itens</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedProposal.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium">{item.description}</p>
                            <p className="text-sm text-slate-600">
                              Quantidade: {item.quantity} • Preço unitário: R$ {item.unit_price.toFixed(2)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">R$ {item.total.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                      
                      <div className="border-t pt-3 mt-3">
                        <div className="flex items-center justify-between font-bold text-lg">
                          <span>Total</span>
                          <span className="text-green-600">
                            R$ {selectedProposal.total_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Terms */}
              {selectedProposal.terms_conditions && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Termos e Condições</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{selectedProposal.terms_conditions}</p>
                  </CardContent>
                </Card>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fechar
              </Button>

              <Button variant="outline" onClick={() => { setShowDetailModal(false); openEdit(selectedProposal); }}>
                ✏️ Editar
              </Button>

              {(selectedProposal.email_history?.length > 0 || selectedProposal.last_email_body) && (
                <Button variant="outline" onClick={() => setHistoryTarget(selectedProposal)} className="text-gray-600">
                  📤 Ver e-mail
                </Button>
              )}
              
              {['draft', 'sent', 'viewed'].includes(selectedProposal.status) && (
                <Button 
                  onClick={() => setEmailTarget(selectedProposal)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  📧 {selectedProposal.status === 'draft' ? 'Enviar Proposta' : 'Reenviar Atualização'}
                </Button>
              )}
              
              {selectedProposal.status === 'accepted' && (
                <Button 
                  onClick={async () => {
                    try {
                      await api.post(`/proposals/${selectedProposal.id}/convert-to-contract`);
                      toast.success('Contrato criado com sucesso! 📄');
                      setShowDetailModal(false);
                      fetchData();
                    } catch (error) {
                      toast.error('Erro ao criar contrato');
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  📄 Converter em Contrato
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}